import {action, computed, observable} from 'mobx';

import apiUserCanUpdatePlanStore from '../api/user/apiUserCanUpdatePlanStore';
import apiUserGetMeStore from '../api/user/apiUserGetMeStore';
import apiCompanySignGetAllStore from '../api/sign/apiCompanySignGetAllStore';
import apiPlanGetAllStore from '../api/plan/apiPlanGetAllStore';
import {
  PAYMENT_PLAN_MONTHLY,
  PAYMENT_PLAN_LIFETIME,
  UPDATE_PLAN_REASON_TRIAL,
  UPDATE_PLAN_REASON_COUPON,
  UPDATE_PLAN_REASON_SUBSCRIPTION_DELETED,
} from '../../constants/subscriptionPlanConstants';
import {cleanUpPlan} from '../../components/common/subscriptionPlanTable/SubscriptionPlanTable';

/**
 * PurchaseFlowStore
 */
class PurchaseFlowStore {
  /**
   * The current step index in the flow.
   *
   * @type {string|null}
   */
  @observable currentStep = null;

  /**
   * Programatically set step
   *
   * @param {string} stepKey
   */
  @action setCurrentStep(stepKey) {
    this.currentStep = stepKey;
  }

  /**
   * The starting plan id.
   * This will clear when the plan is selected.
   *
   * @type {?number}
   */
  @observable startingPlanId = null;

  /**
   * Programatically set startingPlanId
   *
   * @param {number} planId
   */
  @action setStartingPlanId(planId) {
    this.startingPlanId = planId;
  }

  /**
   * The starting credit data.
   * This will clear when the plan or credits are selected.
   *
   * @type {?{
   *  download: number,
   *  request: number
   * }}
   */
  @observable startingAlaCarteCredits = null;

  /**
   * Programatically set startingAlaCarteCredits
   *
   * @param {?{
   *  download: number,
   *  request: number
   * }} alaCarteCredits
   */
  @action setStartingAlaCarteCredits(alaCarteCredits) {
    this.startingAlaCarteCredits = alaCarteCredits;
  }

  /**
   * The a la carte credits the user wants to purchase
   *
   * @type {?{
   *  download: number,
   *  request: number
   * }}
   */
  @observable alaCarteCredits = null;

  /**
   * Programatically set alaCarteCredits
   *
   * @param {?{
   *  download: number,
   *  request: number
   * }} alaCarteCredits
   */
  @action setAlaCarteCredits(alaCarteCredits) {
    this.alaCarteCredits = alaCarteCredits;
  }

  /**
   * The plan.
   *
   * @type {?{}}
   */
  @observable plan = null;

  /**
   * Programatically set plan
   *
   * @param {{}} plan
   */
  @action setPlan(plan) {
    this.plan = plan;
  }

  /**
   * The media stick the user wants to purchase
   *
   * @type {?{
   *  frequency: string,
   *  quantity: number
   * }}
   */
  @observable mediaStickPlan = null;

  /**
   * Programatically set media stick plan
   *
   * @param {{
   *  frequency: string,
   *  quantity: number
   * }} mediaStickPlan
   */
  @action setMediaStickPlan(mediaStickPlan) {
    this.mediaStickPlan = mediaStickPlan;
  }

  /**
   * The order details billing type selected
   *
   * @type {string}
   */
  @observable billingType = PAYMENT_PLAN_MONTHLY;

  /**
   * Programatically set billingType
   *
   * @param {string} billingType
   */
  @action setBillingType(billingType) {
    this.billingType = billingType;
  }

  /**
   * Current user
   *
   * @type {{}}
   */
  @computed get currentUser() {
    return apiUserGetMeStore.data;
  }

  /**
   * Current user's plan
   *
   * @type {?number}
   */
  @computed get currentPlanId() {
    return apiUserGetMeStore.data
      && apiUserGetMeStore.data.projectContentCompany
      && apiUserGetMeStore.data.projectContentCompany.planId
      ? apiUserGetMeStore.data.projectContentCompany.planId
      : undefined;
  }

  /**
   * Flag determining if company needs to update plan
   *
   * @type {boolean}
   */
  @computed get requiresPlanUpdate() {
    return apiUserGetMeStore.data
      ? apiUserGetMeStore.data.requiresPlanUpdate
      : false;
  }

  /**
   * Flag determining if user can update plan
   */
  @computed get canUpdatePlan() {
    return apiUserCanUpdatePlanStore.userCanUpdatePlan
      ? apiUserCanUpdatePlanStore.userCanUpdatePlan.canUpdatePlan
      : false;
  }

  /**
   * Flag determining if user can update ala carte credits
   */
  @computed get canUpdateAlaCarte() {
    return apiUserCanUpdatePlanStore.userCanUpdatePlan
      ? apiUserCanUpdatePlanStore.userCanUpdatePlan.canUpdateAlaCarte
      : false;
  }

  /**
   * Message to display when user cannot update plan
   */
  @computed get cannotUpdatePlanMessage() {
    return apiUserCanUpdatePlanStore.userCanUpdatePlan
      ? apiUserCanUpdatePlanStore.userCanUpdatePlan.message
      : '';
  }

  /**
   * Message to display when user requires a plan update
   */
  @computed get updatePlanMessage() {
    const user = apiUserGetMeStore.data;
    if (!user || !user.requiresPlanUpdate) {
      return '';
    }
    switch (user.planUpdateReason) {
      case UPDATE_PLAN_REASON_TRIAL:
        return 'Your trial has expired. Please update your plan to continue.';
      case UPDATE_PLAN_REASON_COUPON:
        return 'Your coupon has expired. Please update your plan to continue.';
      case UPDATE_PLAN_REASON_SUBSCRIPTION_DELETED:
        return 'Your subscription has ended. Please update your plan to continue.';
      default:
        return 'Your account requires a plan update. Please choose a plan to continue.';
    }
  }

  /**
   * Get users signs
   */
  @computed get userSigns() {
    return apiCompanySignGetAllStore.data;
  }

  /**
   * Store has initialized
   *
   * @type {boolean}
   */
  @observable hasInitialized = false;

  /**
   * Programatically set hasInitialized
   *
   * @param {boolean} hasInitialized
   */
  @action setHasInitialized(hasInitialized) {
    this.hasInitialized = hasInitialized;
  }

  /**
   * Initialize store with params from query string.
   * Resets all values to defaults first, then applies incoming params.
   *
   * @param {{
   *   planId: ?string,
   *   downloadCreditQty: ?string,
   *   requestCreditQty: ?string,
   *   monthlyMediaStickQty: ?string,
   *   lifetimeMediaStickQty: ?string,
   * }} params
   */
  @action async initialize({
    planId,
    downloadCreditQty,
    requestCreditQty,
    monthlyMediaStickQty,
    lifetimeMediaStickQty,
  } = {}) {
    this.setHasInitialized(false);

    // reset to defaults
    this.hasInitialized = false;
    this.currentStep = null;
    this.plan = null;
    this.startingPlanId = null;
    this.alaCarteCredits = null;
    this.startingAlaCarteCredits = null;
    this.mediaStickPlan = null;
    this.billingType = PAYMENT_PLAN_MONTHLY;

    // apply params
    if (planId) {
      apiPlanGetAllStore.refresh(true);
      await apiPlanGetAllStore.getPromise();
      const plans = apiPlanGetAllStore.getFulfilled();
      const matchingPlan = plans.find((p) => p.id === Number(planId));

      if (matchingPlan) {
        this.setPlan(cleanUpPlan(matchingPlan));
        this.setStartingPlanId(Number(planId));
      }
    }

    if (downloadCreditQty || requestCreditQty) {
      const credits = {download: downloadCreditQty, request: requestCreditQty};
      this.setStartingAlaCarteCredits(credits);
      this.setAlaCarteCredits(credits);
    }

    if (monthlyMediaStickQty) {
      this.setMediaStickPlan({frequency: PAYMENT_PLAN_MONTHLY, quantity: Number(monthlyMediaStickQty)});
    } else if (lifetimeMediaStickQty) {
      this.setMediaStickPlan({frequency: PAYMENT_PLAN_LIFETIME, quantity: Number(lifetimeMediaStickQty)});
    }

    // fetch fresh data
    apiUserCanUpdatePlanStore.refresh(true);
    apiUserGetMeStore.refresh(true);
    apiCompanySignGetAllStore.refresh(true);

    await Promise.all([
      apiUserCanUpdatePlanStore.getPromise(),
      apiUserGetMeStore.getPromise(),
      apiCompanySignGetAllStore.getPromise(),
    ]);

    this.setHasInitialized(true);
  }
}

export default new PurchaseFlowStore();
